import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type RunRow } from '../lib/api';

const STATUS_STYLE: Record<RunRow['status'], string> = {
  completed: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  awaiting_user_input: 'bg-amber-100 text-amber-900 border-amber-300',
  failed: 'bg-red-100 text-red-900 border-red-300',
  running: 'bg-blue-100 text-blue-900 border-blue-300',
};

export function RunsPage() {
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [agentFilter, setAgentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const limit = 50;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listRuns({
        limit,
        offset,
        ...(agentFilter ? { agent_name: agentFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      })
      .then((r) => {
        if (cancelled) return;
        setRuns(r.runs);
        setTotal(r.total);
      })
      .catch((e: Error) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [offset, agentFilter, statusFilter]);

  return (
    <div className="space-y-4 max-w-6xl">
      <div>
        <h2 className="font-serif text-3xl font-bold mb-1">Historique des exécutions</h2>
        <p className="text-taupe text-sm">
          Toutes les invocations d'agents pour votre cabinet — runs synchrones et asynchrones, statuts,
          replays et exports.
        </p>
      </div>

      <div className="card flex flex-wrap gap-3 items-end">
        <label className="text-xs">
          <span className="block text-taupe">Agent</span>
          <select
            className="border border-border rounded px-2 py-1.5 text-sm"
            value={agentFilter}
            onChange={(e) => {
              setAgentFilter(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">Tous</option>
            {[
              'onboardeur', 'echeancier', 'comptable', 'facturier', 'recouvreur',
              'rapprocheur', 'fiscaliste', 'clotureur', 'liassier',
              'controleur_dgi', 'veilleur_jort', 'optimisateur',
              'cac_oect', 'drh_cnss', 'juriste_rne', 'tresorier',
            ].map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-taupe">Statut</span>
          <select
            className="border border-border rounded px-2 py-1.5 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">Tous</option>
            <option value="completed">Terminé</option>
            <option value="awaiting_user_input">En attente</option>
            <option value="failed">Échoué</option>
            <option value="running">En cours</option>
          </select>
        </label>
        <div className="ml-auto text-xs text-taupe self-center">
          {total} exécution{total > 1 ? 's' : ''} · page {Math.floor(offset / limit) + 1} / {Math.max(1, Math.ceil(total / limit))}
        </div>
      </div>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-taupe text-sm">Chargement…</div>
        ) : runs.length === 0 ? (
          <div className="p-8 text-center text-taupe text-sm">Aucune exécution sur cette période.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream/40 text-xs uppercase tracking-wider text-taupe border-b border-border">
                <th className="px-3 py-2 text-left">ID</th>
                <th className="px-3 py-2 text-left">Agent</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-right">Latence</th>
                <th className="px-3 py-2 text-left">Démarré</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-border/50 hover:bg-cream/30">
                  <td className="px-3 py-2 font-mono text-xs">{r.id.slice(-12)}</td>
                  <td className="px-3 py-2">{r.agent_name}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{r.latency_ms !== null ? `${r.latency_ms} ms` : '—'}</td>
                  <td className="px-3 py-2 text-xs text-taupe">{formatDate(r.started_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/runs/${r.id}`}
                      className="text-xs px-2 py-1 rounded border border-border hover:border-gold"
                    >
                      Voir
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex justify-between text-xs text-taupe">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - limit))}
          className="px-3 py-1.5 rounded border border-border hover:border-gold disabled:opacity-30"
        >
          ← Précédent
        </button>
        <button
          type="button"
          disabled={offset + limit >= total}
          onClick={() => setOffset(offset + limit)}
          className="px-3 py-1.5 rounded border border-border hover:border-gold disabled:opacity-30"
        >
          Suivant →
        </button>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('fr-FR');
  } catch {
    return iso;
  }
}
